import { PropertyModel } from "../Models";
import { ActionResponse, ActionStatusHelper, RecordMap, useAuditEnricher } from "@/Shared";
import {
    usePublication,
    useMediaFile,
} from "@/OneLiners";
import { useDocumentEnricher } from "@/Domains/Document";
import { useSectorEnricher } from "@/Domains/Country";

export function usePropertyEnricher() {

    const { dataMap: mediaMap, stateResponse: mediaFileResponse } = useMediaFile();
    const { dataMap: publicationMap, stateResponse: publicationResponse } = usePublication();
    const {sectorMap, cityMap, countryMap, response : sectorEnricherResponse} = useSectorEnricher();


    /**
     * Document domain operations
     * Used to enrich mandates with associated documents
     */
    const {
        enricherResponse: documentEnricherResponse,
        findByResource
    } = useDocumentEnricher();

    /**
             * Centralized audit enrichment hook
             * - Provides O(1) audit user resolution via precomputed Map
             * - Exposes audit status and error
             */
            const {
                enricherResponse : auditEnricherResponse,
                userMap,
                getUserById,
                getEnrichedAudit,
            } = useAuditEnricher<PropertyModel>();

    const enricherResponse: ActionResponse = {
        status: ActionStatusHelper.combine(
            auditEnricherResponse.status,
            sectorEnricherResponse.status,
            mediaFileResponse.status,
            publicationResponse.status,
            documentEnricherResponse.status
        ),

        error:  auditEnricherResponse.error ??
            sectorEnricherResponse.error ??
            mediaFileResponse.error ??
            publicationResponse.error ??
            documentEnricherResponse.error
    };

    const getEnrichedPropertyMap = (propertyMap: RecordMap<PropertyModel>): RecordMap<PropertyModel> => {         
        return propertyMap.map<PropertyModel>(getStrictEnrichedProperty);
    }
    

    const getEnrichedPropertyById = (propertyMap : Map<number,PropertyModel>,
        propertyId: number): PropertyModel | undefined => {
        return getEnrichedProperty(propertyMap.get(propertyId));
    }

    const getEnrichedProperty = (property?: PropertyModel): PropertyModel | undefined => {
        return property ? getStrictEnrichedProperty(property) : undefined;
    }

    const getStrictEnrichedProperty = (property: PropertyModel): PropertyModel => {
        const sector = property.sectorId ? sectorMap.get(property.sectorId) : undefined;
        const city = sector?.cityId ? cityMap.get(sector.cityId) : undefined;
        return {
            ...getEnrichedAudit(property),
            sector: sector ? { ...sector, city } : undefined,
        }
    }

    return {
        mediaMap,
        sectorMap,
        cityMap,
        countryMap,
        publicationMap,
        enricherResponse,
        findByDocumentResource : findByResource,
        getEnrichedProperty,
        getStrictEnrichedProperty,
        getEnrichedPropertyById,
        getEnrichedPropertyMap,
    }
}